import { useState, useEffect, useCallback } from 'react';
import {
  Database,
  Server,
  Cloud,
  DollarSign,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Package,
  Tags,
  PieChart,
  TrendingUp,
  Clock,
  ExternalLink,
  Layers,
  Moon,
  Zap,
  Cpu,
  X,
  Globe,
  MapPin,
} from 'lucide-react';
import './CMDB.css';

// ============================================================================
// TYPES
// ============================================================================

interface CMDBProduct {
  id: string;
  name: string;
  description?: string;
  status: string;
  tier: string;
  dataSensitivity: string;
  owner: string;
  team?: string;
  costCenter?: string;
  monthlyBudget?: number;
}

interface CMDBResource {
  id: string;
  name: string;
  provider: string;
  resourceType: string;
  status: string;
  environment: string;
  region?: string;
  tier?: string;
  url?: string;
  productId?: string;
  productName?: string;
  lastSyncedAt: string;
}

interface CMDBProvider {
  id: string;
  provider: string;
  accountName?: string;
  planName?: string;
  planTier?: string;
  monthlyIncluded: Record<string, unknown>;
  isConfigured: boolean;
}

interface CMDBCostByProduct {
  productId: string;
  productName: string;
  monthlyBudget: number;
  totalCost: number;
  budgetUtilizationPct: number;
  resourceCount: number;
}

interface CMDBOverview {
  totalProducts: number;
  totalResources: number;
  totalMonthlyCost: number;
  totalMonthlyBudget: number;
  budgetUtilizationPct: number;
  resourcesByProvider: Record<string, number>;
  resourcesByEnvironment: Record<string, number>;
  resourcesByStatus: Record<string, number>;
  topCostProducts: CMDBCostByProduct[];
  costByProvider: Array<{ provider: string; totalCost: number; resourceCount: number }>;
  alertsTriggered: number;
}

// ============================================================================
// PROVIDER CONFIG
// ============================================================================

const providerConfig: Record<string, { name: string; icon: typeof Cloud; color: string }> = {
  vercel: { name: 'Vercel', icon: Cloud, color: 'blue' },
  railway: { name: 'Railway', icon: Server, color: 'purple' },
  supabase: { name: 'Supabase', icon: Database, color: 'green' },
  neon: { name: 'Neon', icon: Database, color: 'cyan' },
  cloudflare: { name: 'Cloudflare', icon: Cloud, color: 'orange' },
  github: { name: 'GitHub', icon: Layers, color: 'gray' },
  postmark: { name: 'Postmark', icon: Zap, color: 'yellow' },
  anthropic: { name: 'Anthropic', icon: Cpu, color: 'coral' },
  openai: { name: 'OpenAI', icon: Cpu, color: 'teal' },
};

// ============================================================================
// HELPERS
// ============================================================================

function getStatusIcon(status: string) {
  const s = status?.toLowerCase() || '';
  if (s === 'active') return { icon: CheckCircle, className: 'status-active' };
  if (s === 'error') return { icon: XCircle, className: 'status-error' };
  if (s === 'sleeping') return { icon: Moon, className: 'status-sleeping' };
  if (s === 'provisioning') return { icon: RefreshCw, className: 'status-provisioning' };
  if (s === 'inactive') return { icon: AlertTriangle, className: 'status-inactive' };
  return { icon: AlertTriangle, className: 'status-unknown' };
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(amount);
}

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / (1000 * 60));
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return 'Just now';
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function CMDB() {
  const [overview, setOverview] = useState<CMDBOverview | null>(null);
  const [products, setProducts] = useState<CMDBProduct[]>([]);
  const [resources, setResources] = useState<CMDBResource[]>([]);
  const [providers, setProviders] = useState<CMDBProvider[]>([]);
  const [loading, setLoading] = useState({ overview: true, resources: true, sync: false });
  const [error, setError] = useState<string | null>(null);
  const [lastSync, setLastSync] = useState<Date | null>(null);
  const [filterProvider, setFilterProvider] = useState<string>('all');
  const [filterEnvironment, setFilterEnvironment] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [selectedResource, setSelectedResource] = useState<CMDBResource | null>(null);
  const [selectedCMDBProduct, setSelectedCMDBProduct] = useState<CMDBProduct | null>(null);

  // Fetch overview
  const fetchOverview = useCallback(async () => {
    try {
      const res = await fetch('/api/cmdb/overview');
      if (res.ok) {
        const data = await res.json();
        setOverview(data);
      }
    } catch (err) {
      console.error('Error fetching overview:', err);
    } finally {
      setLoading((prev) => ({ ...prev, overview: false }));
    }
  }, []);

  // Fetch products
  const fetchProducts = useCallback(async () => {
    try {
      const res = await fetch('/api/cmdb/products');
      if (res.ok) {
        const data = await res.json();
        setProducts(data.products || []);
      }
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  }, []);

  // Fetch resources
  const fetchResources = useCallback(async () => {
    setLoading((prev) => ({ ...prev, resources: true }));
    try {
      const res = await fetch('/api/cmdb/resources');
      if (res.ok) {
        const data = await res.json();
        setResources(data.resources || []);
      }
    } catch (err) {
      console.error('Error fetching resources:', err);
    } finally {
      setLoading((prev) => ({ ...prev, resources: false }));
    }
  }, []);

  // Fetch providers
  const fetchProviders = useCallback(async () => {
    try {
      const res = await fetch('/api/cmdb/providers');
      if (res.ok) {
        const data = await res.json();
        setProviders(data.providers || []);
      }
    } catch (err) {
      console.error('Error fetching providers:', err);
    }
  }, []);

  // Sync resources from providers
  const syncResources = async () => {
    setLoading((prev) => ({ ...prev, sync: true }));
    setError(null);
    try {
      const res = await fetch('/api/cmdb/resources/sync', { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setLastSync(new Date());
        // Refresh data
        await Promise.all([fetchOverview(), fetchResources()]);
        if (data.errors?.length > 0) {
          setError(`Synced ${data.synced} resources with ${data.errors.length} errors`);
        }
      } else {
        setError('Failed to sync resources');
      }
    } catch (err) {
      console.error('Error syncing resources:', err);
      setError('Failed to sync resources');
    } finally {
      setLoading((prev) => ({ ...prev, sync: false }));
    }
  };

  // Initial load
  useEffect(() => {
    fetchOverview();
    fetchProducts();
    fetchResources();
    fetchProviders();
  }, [fetchOverview, fetchProducts, fetchResources, fetchProviders]);

  // Filtered resources
  const filteredResources = resources.filter((r) => {
    if (filterProvider !== 'all' && r.provider !== filterProvider) return false;
    if (filterEnvironment !== 'all' && r.environment !== filterEnvironment) return false;
    if (filterStatus !== 'all' && r.status !== filterStatus) return false;
    return true;
  });

  // Unique filter values
  const uniqueProviders = [...new Set(resources.map((r) => r.provider))];
  const uniqueEnvironments = [...new Set(resources.map((r) => r.environment))];
  const uniqueStatuses = [...new Set(resources.map((r) => r.status))];

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>CMDB & FinOps</h1>
          <p>Configuration Management Database and cost tracking</p>
        </div>
        <div className="header-actions">
          {lastSync && (
            <span className="last-sync">
              <Clock size={14} />
              Synced {formatTimeAgo(lastSync.toISOString())}
            </span>
          )}
          <button
            className="btn btn-primary"
            onClick={syncResources}
            disabled={loading.sync}
          >
            <RefreshCw size={16} className={loading.sync ? 'spin' : ''} />
            Sync from Providers
          </button>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <AlertTriangle size={16} />
          {error}
        </div>
      )}

      {/* Overview Stats */}
      <div className="grid grid-4 stats-grid">
        <div className="stat-card">
          <div className="stat-icon blue">
            <Package size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{overview?.totalProducts ?? '-'}</span>
            <span className="stat-label">Products</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon purple">
            <Server size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{overview?.totalResources ?? '-'}</span>
            <span className="stat-label">Resources</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon green">
            <DollarSign size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">
              {overview ? formatCurrency(overview.totalMonthlyCost) : '-'}
            </span>
            <span className="stat-label">Monthly Cost</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon yellow">
            <TrendingUp size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">
              {overview?.budgetUtilizationPct ?? 0}%
            </span>
            <span className="stat-label">Budget Used</span>
          </div>
          {overview && overview.budgetUtilizationPct > 80 && (
            <span className="badge badge-failing">Warning</span>
          )}
        </div>
      </div>

      {/* Provider Cards */}
      <div className="grid grid-3 provider-grid">
        {providers.map((provider) => {
          const config = providerConfig[provider.provider] || {
            name: provider.provider,
            icon: Server,
            color: 'gray',
          };
          const Icon = config.icon;
          const resourceCount = overview?.resourcesByProvider[provider.provider] || 0;

          return (
            <div
              key={provider.id}
              className={`provider-card ${config.color} ${!provider.isConfigured ? 'disabled' : ''}`}
            >
              <div className="provider-header">
                <div className={`provider-icon ${config.color}`}>
                  <Icon size={24} />
                </div>
                <div className="provider-status">
                  {provider.isConfigured ? (
                    <span className="badge badge-passing">Connected</span>
                  ) : (
                    <span className="badge badge-muted">Not Configured</span>
                  )}
                </div>
              </div>
              <h3 className="provider-name">{config.name}</h3>
              <div className="provider-details">
                {provider.planName && (
                  <span className="provider-plan">{provider.planName}</span>
                )}
                {provider.accountName && (
                  <span className="provider-account">{provider.accountName}</span>
                )}
              </div>
              <div className="provider-stats">
                <div className="provider-stat">
                  <span className="stat-number">{resourceCount}</span>
                  <span className="stat-label">Resources</span>
                </div>
                {overview?.costByProvider && (
                  <div className="provider-stat">
                    <span className="stat-number">
                      {formatCurrency(
                        overview.costByProvider.find((c) => c.provider === provider.provider)
                          ?.totalCost || 0
                      )}
                    </span>
                    <span className="stat-label">This Month</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-2 cmdb-main">
        {/* Resources Table */}
        <div className="card resources-card">
          <div className="card-header">
            <h2 className="card-title">
              <Layers size={20} />
              Infrastructure Resources
            </h2>
            <div className="resource-filters">
              <select
                value={filterProvider}
                onChange={(e) => setFilterProvider(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Providers</option>
                {uniqueProviders.map((p) => (
                  <option key={p} value={p}>
                    {providerConfig[p]?.name || p}
                  </option>
                ))}
              </select>
              <select
                value={filterEnvironment}
                onChange={(e) => setFilterEnvironment(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Environments</option>
                {uniqueEnvironments.map((e) => (
                  <option key={e} value={e}>
                    {e}
                  </option>
                ))}
              </select>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Status</option>
                {uniqueStatuses.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {loading.resources && resources.length === 0 ? (
            <div className="loading-placeholder">Loading resources...</div>
          ) : filteredResources.length === 0 ? (
            <div className="empty-placeholder">
              <Server size={48} className="placeholder-icon" />
              <h3>No Resources Found</h3>
              <p>Click "Sync from Providers" to discover infrastructure</p>
            </div>
          ) : (
            <div className="resources-table">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Provider</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Environment</th>
                    <th>Product</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredResources.slice(0, 15).map((resource) => {
                    const { icon: StatusIcon, className } = getStatusIcon(resource.status);
                    const config = providerConfig[resource.provider];

                    return (
                      <tr
                        key={resource.id}
                        onClick={() => setSelectedResource(resource)}
                        style={{ cursor: 'pointer' }}
                        className="resource-row"
                      >
                        <td className="resource-name">{resource.name}</td>
                        <td>
                          <span className={`provider-badge ${config?.color || 'gray'}`}>
                            {config?.name || resource.provider}
                          </span>
                        </td>
                        <td className="resource-type">
                          {resource.resourceType.replace('_', ' ')}
                        </td>
                        <td>
                          <span className={`status-badge ${className}`}>
                            <StatusIcon size={12} />
                            {resource.status}
                          </span>
                        </td>
                        <td>
                          <span className="env-badge">{resource.environment}</span>
                        </td>
                        <td>
                          {resource.productName ? (
                            <span className="product-badge">{resource.productName}</span>
                          ) : (
                            <span className="unassigned">Unassigned</span>
                          )}
                        </td>
                        <td>
                          {resource.url && (
                            <a
                              href={resource.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="resource-link"
                            >
                              <ExternalLink size={14} />
                            </a>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {filteredResources.length > 15 && (
                <div className="table-footer">
                  Showing 15 of {filteredResources.length} resources
                </div>
              )}
            </div>
          )}
        </div>

        {/* Cost Breakdown */}
        <div className="card cost-card">
          <div className="card-header">
            <h2 className="card-title">
              <PieChart size={20} />
              Cost by Product
            </h2>
          </div>

          {overview?.topCostProducts && overview.topCostProducts.length > 0 ? (
            <div className="cost-list">
              {overview.topCostProducts.map((product) => (
                <div key={product.productId} className="cost-item">
                  <div className="cost-header">
                    <span className="cost-product">{product.productName}</span>
                    <span className="cost-amount">{formatCurrency(product.totalCost)}</span>
                  </div>
                  <div className="cost-bar-container">
                    <div
                      className={`cost-bar ${
                        product.budgetUtilizationPct > 100
                          ? 'over'
                          : product.budgetUtilizationPct > 80
                          ? 'warning'
                          : 'normal'
                      }`}
                      style={{
                        width: `${Math.min(product.budgetUtilizationPct, 100)}%`,
                      }}
                    />
                  </div>
                  <div className="cost-meta">
                    <span>
                      {product.budgetUtilizationPct}% of{' '}
                      {formatCurrency(product.monthlyBudget)} budget
                    </span>
                    <span>{product.resourceCount} resources</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-placeholder small">
              <DollarSign size={32} className="placeholder-icon" />
              <p>No cost data recorded yet</p>
              <p className="hint">Add cost records via API or integrate billing</p>
            </div>
          )}

          {/* Provider Cost Breakdown */}
          {overview?.costByProvider && overview.costByProvider.length > 0 && (
            <div className="provider-costs">
              <h3>By Provider</h3>
              <div className="provider-cost-list">
                {overview.costByProvider.map((pc) => {
                  const config = providerConfig[pc.provider];
                  return (
                    <div key={pc.provider} className="provider-cost-item">
                      <span className={`provider-badge ${config?.color || 'gray'}`}>
                        {config?.name || pc.provider}
                      </span>
                      <span className="provider-cost-amount">
                        {formatCurrency(pc.totalCost)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Products Grid */}
      <div className="card products-card">
        <div className="card-header">
          <h2 className="card-title">
            <Package size={20} />
            Product Portfolio
          </h2>
        </div>

        <div className="products-grid">
          {products.map((product) => {
            const productResources = resources.filter(
              (r) => r.productId === product.id
            );

            return (
              <div
                key={product.id}
                className="product-item clickable"
                onClick={() => setSelectedCMDBProduct(product)}
              >
                <div className="product-header">
                  <h4 className="product-name">{product.name}</h4>
                  <span className={`status-badge status-${product.status}`}>
                    {product.status}
                  </span>
                </div>
                {product.description && (
                  <p className="product-description">{product.description}</p>
                )}
                <div className="product-meta">
                  <div className="meta-item">
                    <Tags size={14} />
                    <span>{product.tier}</span>
                  </div>
                  <div className="meta-item">
                    <Server size={14} />
                    <span>{productResources.length} resources</span>
                  </div>
                  {product.monthlyBudget && (
                    <div className="meta-item">
                      <DollarSign size={14} />
                      <span>{formatCurrency(product.monthlyBudget)}/mo</span>
                    </div>
                  )}
                </div>
                {product.costCenter && (
                  <div className="product-cost-center">
                    Cost Center: {product.costCenter}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Info Footer */}
      <div className="card info-card">
        <div className="card-header">
          <h2 className="card-title">
            <Zap size={20} />
            FinOps Capabilities
          </h2>
        </div>
        <div className="finops-info">
          <div className="info-section">
            <h4>Resource Discovery</h4>
            <ul>
              <li>Auto-sync from Vercel, Railway, Neon</li>
              <li>Track status and health across providers</li>
              <li>Assign resources to products for allocation</li>
            </ul>
          </div>
          <div className="info-section">
            <h4>Cost Tracking</h4>
            <ul>
              <li>AI API costs: Anthropic &amp; OpenAI usage</li>
              <li>Infrastructure: Vercel, Railway estimates</li>
              <li>Set budgets per product, track utilization</li>
            </ul>
          </div>
          <div className="info-section">
            <h4>Coming Soon</h4>
            <ul>
              <li>Budget alerts via Discord</li>
              <li>Cost forecasting and trends</li>
              <li>Model-level cost attribution</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Resource Detail Modal */}
      {selectedResource && (
        <div className="modal-overlay" onClick={() => setSelectedResource(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-row">
                <h2>{selectedResource.name}</h2>
                <span className={`status-badge ${getStatusIcon(selectedResource.status).className}`}>
                  {selectedResource.status}
                </span>
              </div>
              <button className="close-btn" onClick={() => setSelectedResource(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="detail-label">Provider</span>
                  <span className={`provider-badge ${providerConfig[selectedResource.provider]?.color || 'gray'}`}>
                    {providerConfig[selectedResource.provider]?.name || selectedResource.provider}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Resource Type</span>
                  <span>{selectedResource.resourceType.replace('_', ' ')}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Environment</span>
                  <span className="env-badge">{selectedResource.environment}</span>
                </div>
                {selectedResource.region && (
                  <div className="detail-item">
                    <span className="detail-label">Region</span>
                    <span><MapPin size={14} style={{ marginRight: '4px' }} />{selectedResource.region}</span>
                  </div>
                )}
                {selectedResource.tier && (
                  <div className="detail-item">
                    <span className="detail-label">Tier</span>
                    <span>{selectedResource.tier}</span>
                  </div>
                )}
                <div className="detail-item">
                  <span className="detail-label">Product</span>
                  <span>{selectedResource.productName || 'Unassigned'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Last Synced</span>
                  <span>{formatTimeAgo(selectedResource.lastSyncedAt)}</span>
                </div>
              </div>

              {selectedResource.url && (
                <div className="modal-actions">
                  <a
                    href={selectedResource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                  >
                    <Globe size={16} />
                    Open in Provider
                    <ExternalLink size={14} />
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Product Detail Modal */}
      {selectedCMDBProduct && (
        <div className="modal-overlay" onClick={() => setSelectedCMDBProduct(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-row">
                <h2>{selectedCMDBProduct.name}</h2>
                <span className={`status-badge status-${selectedCMDBProduct.status}`}>
                  {selectedCMDBProduct.status}
                </span>
              </div>
              <button className="close-btn" onClick={() => setSelectedCMDBProduct(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              {selectedCMDBProduct.description && (
                <p style={{ color: 'var(--text-secondary)', marginBottom: '20px' }}>
                  {selectedCMDBProduct.description}
                </p>
              )}

              <div className="detail-grid">
                <div className="detail-item">
                  <span className="detail-label">Tier</span>
                  <span>{selectedCMDBProduct.tier}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Data Sensitivity</span>
                  <span>{selectedCMDBProduct.dataSensitivity}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Owner</span>
                  <span>{selectedCMDBProduct.owner}</span>
                </div>
                {selectedCMDBProduct.team && (
                  <div className="detail-item">
                    <span className="detail-label">Team</span>
                    <span>{selectedCMDBProduct.team}</span>
                  </div>
                )}
                {selectedCMDBProduct.costCenter && (
                  <div className="detail-item">
                    <span className="detail-label">Cost Center</span>
                    <span>{selectedCMDBProduct.costCenter}</span>
                  </div>
                )}
                {selectedCMDBProduct.monthlyBudget && (
                  <div className="detail-item">
                    <span className="detail-label">Monthly Budget</span>
                    <span>{formatCurrency(selectedCMDBProduct.monthlyBudget)}</span>
                  </div>
                )}
              </div>

              <div className="detail-section">
                <h3>Resources ({resources.filter(r => r.productId === selectedCMDBProduct.id).length})</h3>
                <div className="resource-list-mini">
                  {resources
                    .filter(r => r.productId === selectedCMDBProduct.id)
                    .slice(0, 5)
                    .map(r => (
                      <div key={r.id} className="resource-mini-item">
                        <span className={`provider-badge ${providerConfig[r.provider]?.color || 'gray'}`}>
                          {providerConfig[r.provider]?.name || r.provider}
                        </span>
                        <span>{r.name}</span>
                        <span className={`status-badge ${getStatusIcon(r.status).className}`}>
                          {r.status}
                        </span>
                      </div>
                    ))}
                  {resources.filter(r => r.productId === selectedCMDBProduct.id).length > 5 && (
                    <div className="resource-mini-more">
                      +{resources.filter(r => r.productId === selectedCMDBProduct.id).length - 5} more resources
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
